import moment from 'moment';
import { AppointmentData, ClientPack, GeneralSettingsData, Payment, PaymentStatus, PackSettings } from '../../../models';
import { Translate } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getAppointmentTotalPayments, getTotalPrice, serviceName } from '../../../shared/appointmint/appointment';
import { calculateClientPackTotal, isCompletedInvoicePayment } from '../../../shared/invoiceCalculations';
import { formatEuros } from '../../../shared/string';
import { Guid, getNowDateString } from '../../../shared/utils';

export type SourceKind = 'appointment' | 'pack' | 'subscription';

export const QUICK_SOURCE_GROUP_PENDING = 'pending';
export const QUICK_SOURCE_GROUP_AVAILABLE = 'available';

export type QuickSource = {
    id: string;
    kind: SourceKind;
    group: typeof QUICK_SOURCE_GROUP_PENDING | typeof QUICK_SOURCE_GROUP_AVAILABLE;
    label: string;
    amountDue: number;
    appointment?: AppointmentData;
    pack?: ClientPack;
    packTemplate?: PackSettings;
};

export const sourceKey = (source: Pick<QuickSource, 'kind' | 'id'>) => `${source.kind}:${source.id}`;

export const getSourceOptionLabel = (source: QuickSource): string =>
    `${source.label} - ${formatEuros(source.amountDue)}`;

export const buildAppointmentSource = (
    appointment: AppointmentData,
    t: Translate<TK>,
    generalSettings: GeneralSettingsData,
): QuickSource => {
    const total = getTotalPrice(appointment) * (1 + (appointment.vat ?? 0));
    const paid = getAppointmentTotalPayments(appointment);

    return {
        id: appointment.id,
        kind: 'appointment',
        group: QUICK_SOURCE_GROUP_PENDING,
        appointment,
        label: `${t(TK.appointment)} - ${appointment.date?.substring(0, 10)} - ${serviceName(appointment.type, generalSettings)}`,
        amountDue: Math.max(0, total - paid),
    };
};

export const buildPackSource = (pack: ClientPack, t: Translate<TK>): QuickSource => {
    const total = calculateClientPackTotal(pack) * (1 + (pack.vat ?? 0));
    const paid = (pack.payments ?? [])
        .filter(isCompletedInvoicePayment)
        .reduce((sum, payment) => sum + payment.value, 0);

    return {
        id: pack.id,
        kind: pack.isSubscription ? 'subscription' : 'pack',
        group: QUICK_SOURCE_GROUP_PENDING,
        pack,
        label: `${pack.isSubscription ? t(TK.subscription) : t(TK.pack)} - ${pack.packName}`,
        amountDue: Math.max(0, total - paid),
    };
};

export const buildPackTemplateSource = (template: PackSettings, t: Translate<TK>): QuickSource => {
    const isSubscription = !!template.subscriptionUnit;
    return {
        id: template.templateId ?? template.id ?? '',
        kind: isSubscription ? 'subscription' : 'pack',
        group: QUICK_SOURCE_GROUP_AVAILABLE,
        packTemplate: template,
        label: `${isSubscription ? t(TK.subscription) : t(TK.pack)} - ${template.name}`,
        amountDue: template.price,
    };
};

export const buildClientPackFromTemplate = (
    template: PackSettings,
    clientId: string,
    clientName: string,
): ClientPack => {
    const isSubscription = !!template.subscriptionUnit;
    const validityTo = template.validityDays && template.validityDays >= 30
        ? moment().add(template.validityDays / 30, 'months').add(-1, 'day').format('YYYY-MM-DDTHH:mm:ss')
        : template.validityDays
            ? moment().add(template.validityDays - 1, 'days').format('YYYY-MM-DDTHH:mm:ss')
            : undefined;

    return {
        id: '',
        packTemplateId: template.templateId ?? template.id ?? '',
        packName: template.name,
        packVersion: template.version,
        appointmentTypes: template.appointmentTypes,
        price: template.price,
        vat: 0,
        extras: [],
        createdBy: '',
        creationDate: getNowDateString(),
        validityFrom: getNowDateString(),
        validityTo,
        totalMinutesStillAvailable: template.totalMinutes ?? 0,
        totalCreditsStillAvailable: template.totalCredits ?? 0,
        clientId,
        clientName,
        isSubscription,
    };
};

export const buildAppointmentSources = (
    appointments: AppointmentData[],
    t: Translate<TK>,
    generalSettings: GeneralSettingsData,
): QuickSource[] =>
    appointments
        .map(a => buildAppointmentSource(a, t, generalSettings))
        .filter(s => s.amountDue > 0)
        .sort((a, b) => a.label.localeCompare(b.label));

export const buildPackSources = (
    clientPacks: ClientPack[],
    templates: PackSettings[],
    t: Translate<TK>,
): QuickSource[] => {
    const unpaidClientPacks = clientPacks
        .filter(p => !p.isSubscription)
        .map(p => buildPackSource(p, t))
        .filter(s => s.amountDue > 0)
        .sort((a, b) => a.label.localeCompare(b.label));

    const availableTemplates = templates
        .filter(tmpl => !tmpl.subscriptionUnit && !tmpl.disabled)
        .map(tmpl => buildPackTemplateSource(tmpl, t))
        .sort((a, b) => a.label.localeCompare(b.label));

    return [...unpaidClientPacks, ...availableTemplates];
};

export const buildSubscriptionSources = (
    clientPacks: ClientPack[],
    templates: PackSettings[],
    t: Translate<TK>,
): QuickSource[] => {
    const unpaidClientPacks = clientPacks
        .filter(p => !!p.isSubscription)
        .map(p => buildPackSource(p, t))
        .filter(s => s.amountDue > 0)
        .sort((a, b) => a.label.localeCompare(b.label));

    const availableTemplates = templates
        .filter(tmpl => !!tmpl.subscriptionUnit && !tmpl.disabled)
        .map(tmpl => buildPackTemplateSource(tmpl, t))
        .sort((a, b) => a.label.localeCompare(b.label));

    return [...unpaidClientPacks, ...availableTemplates];
};

export const buildManualPackPayment = (
    paymentMethod: string,
    value: number,
    cashRegisterName?: string,
): Payment => ({
    id: Guid.newGuid(),
    date: moment().format('YYYY-MM-DDTHH:mm:ss'),
    type: paymentMethod,
    value,
    status: PaymentStatus.Manual,
    ...(cashRegisterName ? { cashRegisterName } : {}),
});
